import { ErrorBoundary } from "@sentry/react";
import { AgGridReact } from "ag-grid-react";
import { Alert, Button, message, Progress } from "antd";
import {
  ECustomUserTypes,
  GanttActivityTypes,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { ScheduleTaskType } from "pages/submittal-schedule-linking/models";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DateFilter, DateUtils } from "utils/dateutils";
import {
  dummyColumnDef,
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import { QUERY_GET_SCHEDULE_VERSIONS } from "services/graphQL/queries";
import usePageTitle from "hooks/use-page-title";
import {
  GridOptions,
  GetContextMenuItemsParams,
  ColDef,
  ValueFormatterParams,
  MenuItemDef,
  DefaultMenuItem
} from "ag-grid-enterprise";
import ExpandIconPath from "assets/svg/expand-icon.svg";
import CollapseIconPath from "assets/svg/collapse-icon.svg";
import { GridLoadingIndicator } from "components/widgets";
import { getFileFromS3 } from "services/schedule-versions-services";
import { useHistory, useParams } from "react-router";
import {
  getScheduleFileExtension,
  matchCustomUserProjectRole
} from "utils/utils";
import "../project-schedule-details.scss";
import CiqLogPageHeader from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import { FEATURE_ACTIVITY_LIST } from "entity-app/graphQL/ciq-feature-queries";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import ScheduleFilterBar, {
  FilterState,
  EMPTY_FILTERS,
  buildFilterOptionsFromRows
} from "components/schedule-hierarchy/schedule-filter-bar";
import {
  CsvMetadataEntry,
  useCsvFilterMetadata
} from "components/schedule-hierarchy/use-csv-filter-metadata";

type ScheduleTreeRow = any;

function enrichS3ScheduleTask(task: ScheduleTaskType): ScheduleTaskType {
  const enrichedTask = { ...task };
  const taskInfo: any = enrichedTask.$custom_data;
  enrichedTask.critical = taskInfo?.Critical === "1";
  enrichedTask.total_slack = taskInfo?.TotalSlack;
  return enrichedTask;
}

function appendParentOrgChain(
  task: ScheduleTaskType,
  tasksById: Map<string, ScheduleTaskType>,
  org: string[]
): void {
  if (task.parent) {
    const parent = tasksById.get(task.parent);
    if (parent) appendParentOrgChain(parent, tasksById, org);
  }
  org.push(task.id);
}

function appendS3ParentOrgChain(
  task: ScheduleTaskType,
  tasksById: Map<string, ScheduleTaskType>,
  org: string[]
): void {
  const enrichedTask = enrichS3ScheduleTask(task);
  if (enrichedTask.parent) {
    const parent = tasksById.get(enrichedTask.parent);
    if (parent) appendS3ParentOrgChain(parent, tasksById, org);
  }
  org.push(enrichedTask.id);
}

function getCsvMetadataForTask(
  task: ScheduleTaskType,
  metadataMap: Map<string, CsvMetadataEntry>
): CsvMetadataEntry | undefined {
  return (
    metadataMap.get(task.id) ||
    (task.source_task_id ? metadataMap.get(task.source_task_id) : undefined)
  );
}

function applyCsvMetadataToRow(
  row: ScheduleTreeRow,
  task: ScheduleTaskType,
  metadataMap: Map<string, CsvMetadataEntry>,
  isAdvanceFilterEnabled: boolean
): void {
  if (!isAdvanceFilterEnabled || metadataMap.size === 0) return;
  const meta = getCsvMetadataForTask(task, metadataMap);
  if (!meta) return;
  row._phase = meta.phase;
  row._csi_division = meta.csi_division;
  row._activity_type = meta.activity_type;
  row._location_tags = meta.location_tags;
}

function buildActiveScheduleTreeRows(
  tasks: ScheduleTaskType[],
  metadataMap: Map<string, CsvMetadataEntry>,
  isAdvanceFilterEnabled: boolean
): ScheduleTreeRow[] {
  const tasksById = new Map(tasks.map((task) => [task.id, task]));
  return tasks.map((task) => {
    const org: string[] = [];
    if (task) appendParentOrgChain(task, tasksById, org);
    const row: ScheduleTreeRow = { org, ...task };
    applyCsvMetadataToRow(row, task, metadataMap, isAdvanceFilterEnabled);
    return row;
  });
}

function buildS3ScheduleTreeRows(tasks: ScheduleTaskType[]): ScheduleTreeRow[] {
  const tasksById = new Map(tasks.map((task) => [task.id, task]));
  return tasks.map((task) => {
    const enrichedTask = enrichS3ScheduleTask(task);
    const org: string[] = [];
    // enrichS3ScheduleTask always returns a fresh object literal ({ ...task }),
    // which is never falsy, so the else arm of this guard cannot execute.
    /* v8 ignore next */
    if (enrichedTask) appendS3ParentOrgChain(enrichedTask, tasksById, org);
    return { org, ...enrichedTask };
  });
}

function parseInactiveScheduleS3Response(
  responseData: any
): ScheduleTreeRow[] | "error" | undefined {
  if (responseData?.error) return "error";
  if (!responseData?.data?.data) return undefined;
  return buildS3ScheduleTreeRows(responseData.data.data);
}

function FeatureScheduleDetailspage() {
  const { projectId, selectedVersionId }: any = useParams();

  const [selectedfile, setSelectedFile] = useState<any>();
  const { gqlClientForProject, tokenContents, isFeatureFlagEnabled } =
    useContext(ProjectContext);

  const isAdvanceFilterEnabled =
    (isFeatureFlagEnabled("SCHEDULE_ADVANCE_FILTER_V1") ||
      localStorage.getItem("SCHEDULE_ADVANCE_FILTER_V1") === "true") &&
    selectedfile?.is_active;

  const [schFilters, setSchFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [schSearchText, setSchSearchText] = useState("");
  const [showTags, setShowTags] = useState(false);
  const gridRef = useRef<AgGridReact<ScheduleTaskType>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [treeRowData, setTreeRowData] = useState<Array<ScheduleTaskType>>();
  const [displayCount, setDisplayCount] = useState<{
    filtered: number;
    total: number;
  } | null>(null);
  const history = useHistory();

  const {
    metadataMap,
    hasCompletedRecommenderJob = true,
    isRecommenderJobPending = false,
    hasRecommenderJobError = false
  } = useCsvFilterMetadata(
    !!isAdvanceFilterEnabled,
    selectedfile?.is_active
      ? selectedfile?.import_log?.source_values_s3_prefix
      : undefined
  );

  usePageTitle(selectedfile?.schedule_name);

  const { data: scheduleListData } = useCIQQuery(QUERY_GET_SCHEDULE_VERSIONS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  useEffect(() => {
    if (selectedVersionId && scheduleListData?.schedule_import_details) {
      const foundVersion = scheduleListData?.schedule_import_details.find(
        (ver: any) => ver.import_log_id === selectedVersionId
      );

      setSelectedFile(foundVersion);
    } else {
      setSelectedFile(null);
    }
  }, [scheduleListData?.schedule_import_details, selectedVersionId]);

  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const { data: tasksData, loading: taskLoading } = useCIQQuery(
    FEATURE_ACTIVITY_LIST,
    {
      client: gqlClientForProject,
      fetchPolicy: "no-cache",
      skip: !gqlClientForProject
    }
  );

  function setNodesCollapseandExpand(id: string, isExpanded: boolean) {
    gridRef.current?.api.forEachNode((node) => {
      if (node.data?.source_task_id === id) {
        // const isExpanded = !node.expanded;
        gridRef.current?.api.setRowNodeExpanded(node, isExpanded);

        node?.allLeafChildren?.map((leafNode) => {
          gridRef.current?.api.setRowNodeExpanded(
            leafNode,
            leafNode.expanded === isExpanded
              ? leafNode.expanded
              : !leafNode.expanded
          );
        });
      }
    });
  }

  function criticalActivityCellRenderer(params: any) {
    if (
      params.data.critical &&
      params.data.type !== Number(GanttActivityTypes.Wps)
    ) {
      return (
        <div
          className="flex items-center justify-center h-full"
          data-testid="critical-activity-tag-container"
        >
          {CriticalActivityTag()}
        </div>
      );
    }
    return "";
  }

  const isCSVFile = useMemo(() => {
    return getScheduleFileExtension(selectedfile, "csv");
  }, [selectedfile]);

  const progressCellRenderer = ({ data }: any) => {
    const progress = Math.floor(data?.progress || 0);
    return (
      <Progress
        percent={progress}
        steps={4}
        strokeColor="#9E9E9E"
        format={(percent) => {
          return `${percent}%`;
        }}
        data-testid="activity-progress"
      />
    );
  };

  // Cell renderer for activity name with inline tags (when flag enabled)
  const cellWithNameAndTags = useCallback(
    (params: any) => {
      const { data } = params;
      if (!data) return null;
      const isSummary =
        (data as any)._activity_type === "Summary" || data.type === 1;
      const name = data.text || "";
      return (
        <div
          className="flex items-center justify-between w-full gap-2"
          style={{ overflow: "hidden" }}
        >
          <span
            className={`min-w-0 overflow-hidden text-ellipsis whitespace-nowrap ${
              isSummary ? "font-semibold text-gray-900" : "text-gray-700"
            }`}
            style={{ flexShrink: 1 }}
          >
            <TruncatedTextWithTooltip text={name} />
          </span>
          {showTags && data._phase && (
            <div className="flex items-center gap-1 flex-shrink-0 flex-nowrap">
              {data._phase && data._phase !== "Root" && (
                <span
                  className="inline-flex items-center px-2 rounded text-white whitespace-nowrap font-semibold"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax -- categorical phase/location badge color (off-palette)
                    backgroundColor: "#1e40af",
                    fontSize: 10,
                    lineHeight: "18px"
                  }}
                >
                  {data._phase}
                </span>
              )}
              {data._csi_division && (
                <span
                  className="inline-flex items-center px-2 rounded text-white whitespace-nowrap font-semibold"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax -- categorical CSI badge color (off-palette)
                    backgroundColor: "#4b5563",
                    fontSize: 10,
                    lineHeight: "18px"
                  }}
                >
                  {data._csi_division}
                </span>
              )}
              {data._activity_type && data._activity_type !== "Summary" && (
                <span
                  className="inline-flex items-center px-2 rounded text-white whitespace-nowrap font-semibold"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax -- categorical activity-type badge color (off-palette)
                    backgroundColor: "#047857",
                    fontSize: 10,
                    lineHeight: "18px"
                  }}
                >
                  {data._activity_type}
                </span>
              )}
              {data._location_tags && (
                <span
                  className="inline-flex items-center px-2 rounded text-white whitespace-nowrap font-semibold"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax -- categorical phase/location badge color (off-palette)
                    backgroundColor: "#1e40af",
                    fontSize: 10,
                    lineHeight: "18px"
                  }}
                >
                  {data._location_tags}
                </span>
              )}
            </div>
          )}
        </div>
      );
    },
    [showTags]
  );

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    // --- Name & ID columns vary by flag ---
    const nameIdCols: ColDef[] = isAdvanceFilterEnabled
      ? [
          {
            colId: "text",
            field: "text",
            headerName: "Activity Name",
            headerTooltip: "Activity Name",
            showRowGroup: true,
            filter: true,
            flex: 1,
            minWidth: 300,
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: cellWithNameAndTags
            }
          },
          {
            colId: "source_task_id",
            field: "source_task_id",
            headerName: "Activity ID",
            headerTooltip: "Activity ID",
            tooltipField: "source_task_id",
            filter: true,
            minWidth: 150,
            sort: "desc",
            cellRenderer: truncatedTextCellRenderer
          }
        ]
      : [
          {
            colId: "text",
            field: "text",
            headerName: "Activity Name",
            headerTooltip: "Activity Name",
            showRowGroup: true,
            minWidth: 250,
            filter: true,
            cellRenderer: "agGroupCellRenderer"
          },
          {
            colId: "source_task_id",
            field: "source_task_id",
            headerName: "Activity ID",
            headerTooltip: "Activity ID",
            tooltipField: "source_task_id",
            filter: true,
            minWidth: 150,
            sort: "desc"
          }
        ];

    // --- Shared columns (same for both flag ON and OFF) ---
    const sharedCols: ColDef[] = [
      {
        colId: "total_slack",
        field: "total_slack",
        headerName: "Activity Float",
        headerTooltip: "Activity Float",
        cellStyle: (params: any) => {
          if (params?.data?.critical) {
            return { fontWeight: "bold" };
          }
          return { fontWeight: "normal" };
        },
        valueGetter: (params: any) => {
          if (
            params?.data?.total_slack === null ||
            params?.data?.total_slack === undefined ||
            params.data.type === Number(GanttActivityTypes.Wps)
          )
            return "";
          const float = Math.floor((params?.data?.total_slack || 0) / 8);
          return `${float} days`;
        },
        cellRenderer: truncatedTextCellRenderer,
        minWidth: 120,
        getQuickFilterText: () => ""
      },
      {
        colId: "critical",
        field: "critical",
        headerName: "Critical Activity",
        headerTooltip: "Critical Activity",
        cellRenderer: criticalActivityCellRenderer,
        minWidth: 170,
        maxWidth: 170,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params?.value === true ? "Critical" : "Non Critical";
          },
          comparator: (valA: any, valB: any) => {
            if (valA === valB) return 0;
            return valA > valB ? -1 : 1;
          }
        },
        getQuickFilterText: () => ""
      },
      {
        colId: "start_date",
        field: "$raw.Start",
        headerName: "Planned Start Date",
        headerTooltip: "Planned Start Date",
        valueGetter: ({ data }: any) => {
          if (selectedfile?.is_active) {
            return data?.start_date
              ? DateUtils.format(data?.start_date, "MM-DD-YYYY")
              : "";
          }
          return data?.$raw?.Start
            ? DateUtils.format(data?.$raw?.Start, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 120,
        maxWidth: 170,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "end_date",
        field: "$raw.Finish",
        headerName: "Planned End Date",
        headerTooltip: "Planned End Date",
        valueGetter: ({ data }: any) => {
          if (selectedfile?.is_active) {
            return data?.end_date
              ? DateUtils.format(data?.end_date, "MM-DD-YYYY")
              : "";
          }
          return data?.$raw?.Finish
            ? DateUtils.format(data?.$raw?.Finish, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 120,
        maxWidth: 170,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "duration",
        field: "duration",
        headerName: "Duration",
        headerTooltip: "Duration",
        minWidth: 100,
        maxWidth: 100,
        filter: true,
        valueGetter: (params: any) => {
          const duration = Math.floor((params?.data?.duration || 0) / 8);
          return duration;
        },
        getQuickFilterText: () => ""
      },
      {
        colId: "actual_start_date",
        field: "actual_start_date",
        headerName: "Actual Start Date",
        headerTooltip: "Actual Start Date",
        valueGetter: ({ data }: any) => {
          return data?.actual_start_date
            ? DateUtils.format(data?.actual_start_date, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 160,
        maxWidth: 160,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "actual_finish_date",
        field: "actual_finish_date",
        headerName: "Actual End Date",
        headerTooltip: "Actual End Date",
        valueGetter: ({ data }: any) => {
          return data?.actual_finish_date
            ? DateUtils.format(data?.actual_finish_date, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "progress",
        headerName: "Progress",
        headerTooltip: "Progress",
        cellRenderer: progressCellRenderer,
        minWidth: 150
      }
    ];

    // --- Hidden filter columns (only when flag ON) ---
    const hiddenFilterCols: ColDef[] = isAdvanceFilterEnabled
      ? [
          {
            colId: "phase",
            field: "_phase",
            headerName: "Phase",
            hide: true,
            filter: "agSetColumnFilter",
            suppressFiltersToolPanel: false
          },
          {
            colId: "activity_type",
            field: "_activity_type",
            headerName: "Activity Type",
            hide: true,
            filter: "agSetColumnFilter",
            suppressFiltersToolPanel: false
          },
          {
            colId: "csi_division",
            field: "_csi_division",
            headerName: "CSI Division",
            hide: true,
            filter: "agSetColumnFilter",
            suppressFiltersToolPanel: false
          },
          {
            colId: "location_tags",
            field: "_location_tags",
            headerName: "Location",
            hide: true,
            filter: "agSetColumnFilter",
            suppressFiltersToolPanel: false
          }
        ]
      : [];

    const colArr: ColDef[] = [
      dummyColumnDef,
      ...nameIdCols,
      ...sharedCols,
      ...hiddenFilterCols
    ];

    if (!isGCUser || isCSVFile) {
      const csvFileSkippedColumns = [
        "total_slack",
        "critical",
        "actual_start_date",
        "actual_finish_date",
        "progress"
      ];
      return colArr.filter(
        (col: ColDef) => !csvFileSkippedColumns.includes(col?.colId ?? "")
      );
    }
    return colArr;
  }, [
    isAdvanceFilterEnabled,
    cellWithNameAndTags,
    isCSVFile,
    isGCUser,
    selectedfile?.is_active
  ]);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const result: MenuItemDef[] = [
        {
          // custom item
          name: "Expand All",
          action: () => {
            setNodesCollapseandExpand(
              params?.node?.data?.source_task_id || "",
              true
            );
          },
          icon: `<img src='${ExpandIconPath}'>`
        },
        {
          // custom item
          name: "Collapse All",
          action: () => {
            setNodesCollapseandExpand(
              params?.node?.data?.source_task_id || "",
              false
            );
          },
          icon: `<img src='${CollapseIconPath}'>`
        }
      ];

      return (params?.node?.allLeafChildren || []).length > 1 ? result : [];
    },
    []
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: false,
      menuTabs: [],
      resizable: true,
      editable: false,
      cellRendererParams: {
        suppressCount: true
      },
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const gridOptions: GridOptions = {
    defaultColDef,
    rowSelection: {
      mode: "singleRow",
      copySelectedRows: false
    },
    getContextMenuItems
  };

  // Rows for the filter bar's cascading logic (non-summary only)
  const filterBarRows = useMemo(() => {
    if (!isAdvanceFilterEnabled || !treeRowData) return [];
    return treeRowData
      .filter(
        (r: any) => r.type !== 1 && r.type !== Number(GanttActivityTypes.Wps)
      )
      .map((r: any) => ({
        phase: r._phase,
        csi_division: r._csi_division,
        activity_type: r._activity_type,
        location_tags: r._location_tags,
        activity_name: r.text,
        activity_id: r.source_task_id || r.id
      }));
  }, [treeRowData, isAdvanceFilterEnabled]);

  const enrichedFilterOptions = useMemo(
    () => buildFilterOptionsFromRows(filterBarRows),
    [filterBarRows]
  );

  // Track whether user has interacted with filters (skip first render)
  const filtersInitialized = useRef(false);

  // Sync filter bar selections → AG Grid hidden column filters
  useEffect(() => {
    if (!isAdvanceFilterEnabled) return;
    const api = gridRef.current?.api;
    if (!api) return;

    const hasFilters =
      schFilters.phase.length > 0 ||
      schFilters.csiDivision.length > 0 ||
      schFilters.activityType.length > 0 ||
      schFilters.location.length > 0;
    const hasSearch = schSearchText.trim().length > 0;

    // Skip first render — let groupDefaultExpanded handle initial state
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      // The false arm is unreachable: this block runs only on the first effect
      // pass (filtersInitialized just flipped to true), and schFilters/schSearchText
      // can only change via UI that exists after that first pass — so on this pass
      // hasFilters/hasSearch are always empty.
      /* v8 ignore next */
      if (!hasFilters && !hasSearch) return;
    }

    const applyFilters = async () => {
      const cols: Array<{ colId: string; values: string[] }> = [
        { colId: "phase", values: schFilters.phase },
        { colId: "activity_type", values: schFilters.activityType },
        { colId: "csi_division", values: schFilters.csiDivision },
        { colId: "location_tags", values: schFilters.location }
      ];

      for (const { colId, values } of cols) {
        const filterInstance = await api.getColumnFilterInstance(colId);
        if (filterInstance) {
          filterInstance.setModel(values.length > 0 ? { values } : null);
        }
      }

      // Apply search via quick filter
      if (schSearchText.trim()) {
        api.setGridOption("quickFilterText", schSearchText.trim());
      } else {
        api.setGridOption("quickFilterText", "");
      }

      api.onFilterChanged();

      // Auto-expand when filters active, collapse when cleared
      if (hasFilters || hasSearch) {
        api.expandAll();
      } else {
        api.collapseAll();
      }
    };

    applyFilters();
  }, [isAdvanceFilterEnabled, schFilters, schSearchText]);

  // Track displayed row count after AG Grid filters change
  const handleFilterChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !isAdvanceFilterEnabled) return;
    // Skip if data hasn't loaded yet
    if (!treeRowData || treeRowData.length === 0) return;

    let filtered = 0;

    api.forEachNodeAfterFilter((node) => {
      if (!node.data) return;
      if (
        node.data.type !== 1 &&
        node.data.type !== Number(GanttActivityTypes.Wps)
      ) {
        filtered++;
      }
    });

    const total = treeRowData.filter(
      (r: any) => r.type !== 1 && r.type !== Number(GanttActivityTypes.Wps)
    ).length;
    setDisplayCount((prev) => {
      if (prev && prev.filtered === filtered && prev.total === total)
        return prev;
      return { filtered, total };
    });
  }, [isAdvanceFilterEnabled, treeRowData]);

  useEffect(() => {
    if (!selectedfile) return;
    const filepath = selectedfile?.import_log?.resolved_values_s3_prefix;
    if (selectedfile?.is_active) {
      if (tasksData) {
        const filteredTasks =
          tasksData?.getScheduleDataByProject?.filter(
            (ts: ScheduleTaskType) => ts.type !== 3 && ts.type !== 4
          ) ?? [];
        const tree = buildActiveScheduleTreeRows(
          filteredTasks,
          metadataMap,
          !!isAdvanceFilterEnabled
        );
        setTreeRowData(tree);
        gridRef?.current?.api?.setGridOption("loading", false);
      } else if (!taskLoading) {
        gridRef?.current?.api?.setGridOption("loading", false);
        gridRef?.current?.api?.showNoRowsOverlay();
        setTreeRowData([]);
      }
    } else if (filepath) {
      getFileFromS3(filepath).then((responseData) => {
        const result = parseInactiveScheduleS3Response(responseData);
        if (result === "error") {
          gridRef?.current?.api?.setGridOption("loading", false);
          gridRef?.current?.api?.showNoRowsOverlay();
          setTreeRowData([]);
          message.error("Unable to view selected schedule, please try again.");
          return;
        }
        if (result) {
          setTreeRowData(result);
          gridRef?.current?.api?.setGridOption("loading", false);
        }
      });
    } else {
      gridRef?.current?.api?.setGridOption("loading", false);
      gridRef?.current?.api?.showNoRowsOverlay();
    }
  }, [
    tasksData,
    selectedfile,
    taskLoading,
    isAdvanceFilterEnabled,
    metadataMap
  ]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const getDataPath = useMemo(() => {
    return (data: any) => {
      return data.org;
    };
  }, []);

  const headerItems = useMemo(() => {
    const items = [
      <CiqAgSearch
        key="Schedules_search"
        gridRef={gridRef}
        placeholder="Search Schedules"
        data-testid="schedule-search"
        {...(isAdvanceFilterEnabled
          ? { onSearch: (text: string) => setSchSearchText(text) }
          : {})}
      />
    ];
    if (!selectedfile?.is_active) {
      items.push(
        <Button
          type="primary"
          hidden={isPermissionNotGrantted(
            ProjectPermissionEnum.ImportSchedule,
            tokenContents?.role ?? ""
          )}
          onClick={() => {
            history.push(
              `/project/${projectId}/schedule/change-impact?&selectedVersionId=${selectedfile.import_log.id}`
            );
          }}
          data-testid="compare-with-active-button"
        >
          Compare with active version
        </Button>
      );
    }
    return items;
  }, [
    history,
    isAdvanceFilterEnabled,
    projectId,
    selectedfile?.import_log.id,
    selectedfile?.is_active,
    tokenContents?.role
  ]);

  return (
    <div className="flex flex-col w-full h-full">
      <CiqLogPageHeader
        entityName={
          isAdvanceFilterEnabled ? (
            <span className="flex items-center gap-2">
              {selectedfile?.schedule_name || ""}
              <span className="text-sm font-normal text-gray-500">
                {displayCount
                  ? displayCount.filtered < displayCount.total
                    ? `(${displayCount.filtered.toLocaleString()} of ${displayCount.total.toLocaleString()})`
                    : `(${displayCount.total.toLocaleString()})`
                  : treeRowData
                    ? `(${treeRowData.filter((r: any) => r.type !== 1 && r.type !== Number(GanttActivityTypes.Wps)).length.toLocaleString()})`
                    : ""}
              </span>
            </span>
          ) : (
            selectedfile?.schedule_name || ""
          )
        }
        items={headerItems}
        backButton={() => {
          history.replace(`/project/${projectId}/schedule/project-schedules`);
        }}
        data-testid="schedule-details-header"
      />

      <ErrorBoundary>
        {isAdvanceFilterEnabled && hasCompletedRecommenderJob && (
          <ScheduleFilterBar
            filters={schFilters}
            onFilterChange={setSchFilters}
            onExpandAll={() => gridRef.current?.api?.expandAll()}
            onCollapseAll={() => gridRef.current?.api?.collapseAll()}
            showTags={showTags}
            onShowTagsChange={setShowTags}
            totalCount={
              displayCount?.total ??
              (treeRowData || []).filter(
                (r: any) =>
                  r.type !== 1 && r.type !== Number(GanttActivityTypes.Wps)
              ).length
            }
            filteredCount={
              displayCount?.filtered ??
              (treeRowData || []).filter(
                (r: any) =>
                  r.type !== 1 && r.type !== Number(GanttActivityTypes.Wps)
              ).length
            }
            filterOptions={enrichedFilterOptions}
            allRows={filterBarRows}
            searchText={schSearchText}
            hideCount
          />
        )}
        {isAdvanceFilterEnabled && isRecommenderJobPending && (
          <div className="px-6 py-2" data-testid="link-recommender-pending">
            <Alert
              type="info"
              showIcon
              title="AI analysis is still running in the background. Filters will appear when it's ready."
            />
          </div>
        )}
        {isAdvanceFilterEnabled && hasRecommenderJobError && (
          <div className="px-6 py-2" data-testid="link-recommender-error">
            <Alert
              type="error"
              showIcon
              title="AI analysis failed. Filters are unavailable."
            />
          </div>
        )}
        <div className="flex flex-col h-full w-full ag-theme-alpine">
          <AgGridReact<ScheduleTaskType>
            ref={gridRef}
            rowData={treeRowData}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            animateRows={false}
            onGridReady={() => {
              setGridReady(true);
              gridRef.current?.api.sizeColumnsToFit();
            }}
            loadingOverlayComponent={GridLoadingIndicator}
            rowSelection={{ mode: "singleRow", checkboxes: false }}
            treeData
            groupDefaultExpanded={isAdvanceFilterEnabled ? 2 : -1}
            getDataPath={getDataPath}
            groupDisplayType="custom"
            suppressCellFocus
            suppressDragLeaveHidesColumns
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            tooltipShowMode="whenTruncated"
            excludeChildrenWhenTreeDataFiltering
            onFilterChanged={handleFilterChanged}
          />
        </div>
      </ErrorBoundary>
    </div>
  );
}

export default FeatureScheduleDetailspage;
